import * as React from 'react';
import { connect } from 'react-redux';
import { Rectangle } from 'ark-common';
import { Publication } from '../../types/epub';
import {
  getDefaultRendition,
  toOPFBasedPath,
  getReflowableRendition,
} from '../../utils/epub';
import LinearPage from './tabpage/LinearPage';
import TabPanel from './TabPanel';
import {
  PagesState, TabInfo, TabType, makePopupPanelID, isPopupPanelID, makeTabIDFromKey, tabFocusSelf,
} from '../../reducers/pages';
import {
  openNewTab, replaceContent, closeTab, openPopup, closePopup,
  openGeneratedContent, moveToPage, goToReference, setFocus,
} from '../../actions/PageAction';
import { Dispatch, Action } from 'redux';
import { AppStates } from '../../reducers/app';
import { IntegratedDrawingState } from '../../reducers/integratedDrawing';
import { SimpleResizeSensor } from '../common/SimpleResizeSensor';
import { blankDrawingState } from '../../drawing/reducers/drawing';
import { resolveURL } from '../../../../../common/src/ts/utils';
import { PreferenceState } from '../../reducers/preference';
import { DrawingMode } from '../../drawing/types';
import { drawingAreaMargin, fundamentalSizeMultiplier } from '../../drawing/constants';
import GeneratedReflowablePage from './tabpage/GeneratedReflowablePage';
import GeneratedFixedLayoutPage from './tabpage/GeneratedFixedLayoutPage';
import { discardHistory } from '../../actions/IntegratedDrawingAction';
import { setTabPanelAreaRect, setContentSize, setPopupRect } from '../../actions/ViewAction';
import { createDefaultUIEventListeners, createMarkerUIEventListeners } from '../../helpers/uievent';
import { UIModeState } from '../../reducers/uimode';
import { setUIMode } from '../../actions/UIModeAction';
import { createPageIDForTab } from '../../helpers/integratedDrawings';
import { DrawingAction, isSingleAddingAction } from '../../drawing/actions/DrawingAction';
import DrawingToolAction from '../../drawing/actions/DrawingToolAction';
import DrawingCanvasOnTab from './DrawingCanvasOnTab';
import SingleItemView from './SingleItemView';
import { DrawingToolState, PastingImage } from '../../drawing/reducers/drawingTool';
import { ContentToViewer } from '../../types/messages';
import ContentStorageAction from '../../actions/ContentStorageAction';
import { PictureStorageState } from '../../drawing/reducers/pictureStorage';
import { UIMode } from '../../../../../common/src/ts/types/UIMode';
import { setSelectionState } from '../../actions/SelectionAction';
import { SelectionType } from '../../reducers/selection';
import { ViewState } from '../../reducers/view';
import { UIEventListeners } from '../../../../../common/src/ts/types/uievent';
import { MarkerToolState } from '../../marker/reducers/marker';
import Popup from './Popup';
import { MarkerInfo } from '../../marker';
import { getGlobalFrameStore } from '../../services/FrameStore';
import {
  MultiroleLinkInfo, MultiroleLinkKind,
} from '../../../../../common/src/ts/types/multiroleLink';
import { isWindow } from '../../../../../common/src/ts/utils/dom';
import { zIndex } from '../../constants';
import { getTitle, getFaviconPath } from '../../helpers/renditions';
import { PageAction } from '../../../../..';
import getStamps from '../../utils/getStamps';

interface TabbedViewerProps {
  pagesState: PagesState;
  preferenceState: PreferenceState;
  drawing: boolean;
  drawingState: IntegratedDrawingState;
  drawingToolState: DrawingToolState;
  pictureStorage: PictureStorageState<HTMLCanvasElement>;
  uimodeState: UIModeState;
  tabPanelAreaRect: Rectangle;
  dispatch: Dispatch<Action>;
  publication: Publication | undefined;
  installedComponents: ReadonlyArray<string>;
  onBackToBookshelf: () => void;
  onSaveWorkspace: () => void;
  onOpenFile: (filePath: string) => void;
  viewState: ViewState;
  markerTool: Readonly<MarkerToolState>;
  markers: MarkerInfo[];
}

const generatedContentDefaultTitle = '拡張コンテンツ';

const style: React.CSSProperties = {
  display: 'flex',
  flexGrow: 1,
  overflow: 'hidden',
  position: 'relative',
  zIndex: zIndex.tabbedViewer,
};

const modelessPopupContainerStyle: React.CSSProperties = {
  position: 'relative',
  zIndex: zIndex.modelessPopupContainer,
};

class TabbedViewer extends React.Component<TabbedViewerProps> {
  public readonly margin: number;

  public constructor(props: TabbedViewerProps) {
    super(props);
    this.margin = drawingAreaMargin;
  }

  public componentDidMount(): void {
    window.addEventListener('message', this.handleIFrameMessage);
  }

  public componentWillUnmount(): void {
    window.removeEventListener('message', this.handleIFrameMessage);
  }

  private closeTab(tabID: string) {
    this.props.dispatch(closeTab(tabID));
    this.props.dispatch(discardHistory(tabID));
  }

  private handleResize = (clientRect: Rectangle): void => {
    // XXX : Assumes that x and y won't change unless the area is resized.
    this.props.dispatch(setTabPanelAreaRect(clientRect));
  }

  /*
   * FIXME: This function addresses messages from not only tabbed pages but also popups. Thus it
   * should be moved to "App" layer.
   */
  private handleIFrameMessage = (ev: MessageEvent): void => {
    const message = ev.data;

    if (
      !ContentToViewer.isDomesticMessage(message)
      || ev.source === null
      || !isWindow(ev.source)
    ) {
      return;
    }

    const { pagesState, publication, dispatch } = this.props;
    if (publication === undefined) {
      return;
    }

    switch (message.type) {
      case ContentToViewer.MessageType.OpenPopup: {
        const path = toOPFBasedPath(
          publication,
          resolveURL(message.data.path, message.data.baseURI),
        );
        const title = getTitle(publication.renditions, path);
        const parent = pagesState.tabs[pagesState.activeTab].id;
        const [id, zIndex] = makePopupPanelID(pagesState, parent, message.data.key);

        const {
          modal, resizable, titlebar, frame,
          x, y, width, height, minWidth, minHeight, maxWidth, maxHeight, collapsed,
        } = message.data;

        try {
          dispatch(openPopup({
            parent, id, path, title, zIndex,
            modal, resizable, titlebar, frame,
            x, y, width, height, minWidth, minHeight, maxWidth, maxHeight, collapsed,
          }));
        } catch (error) {
          alert(error.message);
        }
        break;
      }
      case ContentToViewer.MessageType.ClosePopup: {
        let popupID: string;
        if ('key' in message.data) {
          const parent = pagesState.tabs[pagesState.activeTab].id;
          popupID = makePopupPanelID(pagesState, parent, message.data.key)[0];
        } else {
          popupID = message.data.panelID;
        }
        dispatch(closePopup(popupID));
        break;
      }
      case ContentToViewer.MessageType.OpenTab: {
        const path = toOPFBasedPath(
          publication,
          resolveURL(message.data.path, message.data.baseURI),
        );

        const { key, closeSelf, srcPanelID } = message.data;
        if (closeSelf) {
          if (isPopupPanelID(srcPanelID)) {
            dispatch(PageAction.closePopup(srcPanelID));
          } else {
            dispatch(PageAction.closeTab(srcPanelID));
          }
        }

        switch (key) {
          case 'main': {
            const page = getDefaultRendition(publication).itemIndices[path];
            if (page !== undefined) {
              if (page.linear) {
                // FIXME: 数字直書き
                moveToPage(0, page.linearIndex, this.props.dispatch, message.data.active);
              } else {
                alert('Navigation to non-linear or non-default-rendition item.');
              }
            } else {
              window.open(path);
            }
            break;
          }
          case 'self': {
            try {
              dispatch(
                replaceContent(
                  path,
                  getTitle(publication.renditions, path),
                  getFaviconPath(publication.renditions, path, publication.resourceDir),
                ),
              );
            } catch (error) {
              alert(error.message);
            }
            break;
          }
          default: {
            try {
              dispatch(
                openNewTab(
                  path,
                  getTitle(publication.renditions, path),
                  getFaviconPath(publication.renditions, path, publication.resourceDir),
                  message.data.active, message.data.key,
                ),
              );
            } catch (error) {
              alert(error.message);
            }
            break;
          }
        }

        break;
      }
      case ContentToViewer.MessageType.CloseTab: {
        let tabID: string;
        if ('key' in message.data) {
          tabID = makeTabIDFromKey(message.data.key);
        } else {
          if (isPopupPanelID(message.data.panelID)) {
            break; // Just ignore invalid API uses.
          }
          tabID = message.data.panelID;
        }
        this.closeTab(tabID);
        break;
      }
      case ContentToViewer.MessageType.SetPopupRect:
        dispatch(setPopupRect(message.data.panelID, message.data.rect));
        break;
      case ContentToViewer.MessageType.PutReflowableContent:
        dispatch(openGeneratedContent(
          message.data.key,
          message.data.title || generatedContentDefaultTitle,
          message.data.content,
        ));
        break;
      case ContentToViewer.MessageType.PutFixedLayoutContent:
        dispatch(openGeneratedContent(
          message.data.key,
          message.data.title || generatedContentDefaultTitle,
          message.data.content,
          message.data,
        ));
        break;
      case ContentToViewer.MessageType.NotifyContentSize:
        dispatch(setContentSize(message.data.panelID, message.data.width, message.data.height));
        break;
      case ContentToViewer.MessageType.PutContentData:
        if (message.data.value === undefined) {
          dispatch(ContentStorageAction.remove(message.data.key));
        } else {
          dispatch(ContentStorageAction.put(message.data.key, message.data.value));
        }
        break;
      case ContentToViewer.MessageType.MarkerClicked:
        if (message.data.id === undefined) {
          dispatch(setSelectionState({ type: SelectionType.NONE }));
        } else {
          dispatch(setSelectionState({ type: SelectionType.MARKER, id: message.data.id }));
        }
        break;
      case ContentToViewer.MessageType.PostMessage:
        getGlobalFrameStore().broadcastContentMessage(message.data);
        break;
      default:
        // Just ignore other messages.
        break;
    }
  }

  private handleDrawingAction = (action: DrawingAction) => {
    this.props.dispatch(action);
    if (isSingleAddingAction(action)) {
      setUIMode(UIMode.Touch, this.props.dispatch);
    }
  }

  private handleMultiroleLinkClick = (multiroleLink: MultiroleLinkInfo) => {
    switch (multiroleLink.kind) {
      case MultiroleLinkKind.File:
        this.props.onOpenFile(multiroleLink.file);
        break;
      case MultiroleLinkKind.Web:
        window.open(multiroleLink.url, multiroleLink.title);
        break;
      case MultiroleLinkKind.Page:
        this.props.dispatch(goToReference(multiroleLink.page));
        break;
    }
  }

  private handleSetPastingImagesAction = (pastingImages: ReadonlyArray<PastingImage>) => {
    this.props.dispatch(DrawingToolAction.setPastingImages(pastingImages));
  }

  private handleRequestFocus = () => {
    this.props.dispatch(setFocus(tabFocusSelf));
  }

  private generateDrawingCanvas(
    tabID: string,
    pageID: string,
  ) {
    const drawingData = this.props.drawingState.currentData[pageID];
    const tab = this.props.pagesState.tabs[this.props.pagesState.activeTab];
    return (
      <DrawingCanvasOnTab
        stamps={getStamps(this.props.publication)}
        key={'drawing-canvas'}
        panelID={tabID}
        margin={this.margin * fundamentalSizeMultiplier}
        focus={tab.focus === tabFocusSelf}
        toolState={{
          ...this.props.drawingToolState,
          mode: this.props.drawing ? this.props.drawingToolState.mode : DrawingMode.Disabled,
        }}
        data={(drawingData === undefined) ? blankDrawingState : drawingData}
        pictureStorage={this.props.pictureStorage}
        onSetPastingImages={this.handleSetPastingImagesAction}
        onMultiroleLinkClick={this.handleMultiroleLinkClick}
        onRequestFocus={this.handleRequestFocus}
        onDrawingAction={this.handleDrawingAction} />
    );
  }

  private generateTabContent = (tab: TabInfo): JSX.Element => {
    const { props } = this;
    const { uimodeState: uimode, publication, dispatch } = props;
    if (publication === undefined) {
      return <></>;
    }

    const pageID = createPageIDForTab(tab, publication);
    let uiEventListeners: UIEventListeners;
    if (uimode.mode === UIMode.Marker) {
      uiEventListeners = createMarkerUIEventListeners(props.markerTool, dispatch);
    } else {
      uiEventListeners = createDefaultUIEventListeners(dispatch, tab.id);
    }

    const { tabPanelAreaRect } = this.props;
    switch (tab.type) {
      case TabType.LinearPages: {
        const pageProps = {
          uiEventListeners,
          uimode,
          tabID: tab.id,
          rootDir: publication.resourceDir,
          rendition: getDefaultRendition(publication),
          installedComponents: this.props.installedComponents,
          page: tab.currentPage,
        };
        return (
          <>
            <LinearPage {...pageProps} />
            {this.generateDrawingCanvas(tab.id, pageID)}
          </>
        );
      }

      case TabType.ExtraPage: {
        const rendition = getDefaultRendition(publication);
        const item = rendition.itemIndices[tab.path];
        const pageProps = {
          item,
          uiEventListeners,
          uimode,
          rootDir: publication.resourceDir,
          installedComponents: this.props.installedComponents,
          panelID: tab.id,
          opf: rendition.opf,
          topLevel: false,
          collapsed: false, // Tab is never collapsed.
          shouldApplyReflowableContentSettings: false,
        };

        return (
          <>
            <SingleItemView {...pageProps} />
            {item.fxl ? this.generateDrawingCanvas(tab.id, pageID) : null}
          </>
        );
      }

      case TabType.ReflowablePages: {
        const rendition = getReflowableRendition(publication);
        const item = rendition.linearItems[tab.currentPage];
        const pageProps = {
          item,
          uiEventListeners,
          uimode,
          panelID: tab.id,
          rootDir: publication.resourceDir,
          installedComponents: this.props.installedComponents,
          opf: rendition.opf,
          topLevel: false,
          collapsed: false, // Tab is never collapsed.
          shouldApplyReflowableContentSettings: true,
        };
        return (
          <SingleItemView {...pageProps} />
        );
      }

      case TabType.GeneratedContent: {
        const pageProps = {
          uimode,
          uiEventListeners,
          panelID: tab.id,
          pageAreaWidth: tabPanelAreaRect.width,
          pageAreaHeight: tabPanelAreaRect.height,
          title: tab.title,
          xhtml: tab.xhtml,
        };

        if (tab.params != null) {
          const fxlPageProps = {
            ...pageProps,
            width: tab.params['width'],
            height: tab.params['height'],
            scaleBehavior: tab.params['scaling'],
          };

          return <>
            <GeneratedFixedLayoutPage {...fxlPageProps} />
            {this.generateDrawingCanvas(tab.id, pageID)}
          </>;
        }

        return (
          <GeneratedReflowablePage {...pageProps} />
        );
      }
    }
  }

  // ツールバーより下の層、タブ領域内に表示するポップアップを生成
  private generatePopups(tab: TabInfo): JSX.Element {
    const { popups } = this.props.pagesState;
    const modelessPopups: JSX.Element[] = [];
    const modalPopups: JSX.Element[] = [];

    for (const id in popups) {
      const popup = popups[id];
      if (popup.parent !== tab.id || popup.topLevel) {
        continue;
      }

      const handleChangeBounds = (rect: Rectangle) => {
        this.props.dispatch(setPopupRect(id, rect));
      };

      // TODO: UIEventListenersを使い分けて連続マーカーに対応させる
      // * 連続マーカーに対応させる時はFrameStore#frameFromPoint()をポップアップ対応に改修する必要あり
      const popupElement = (
        <Popup
          key={id}
          popupID={id}
          focus={tab.focus === id}
          content={popup}
          uimode={this.props.uimodeState}
          uiEventListeners={createDefaultUIEventListeners(this.props.dispatch, id)}
          onChangeBounds={handleChangeBounds}
          onCloseButtonClick={() => this.props.dispatch(closePopup(id))}
          onOpenFile={this.props.onOpenFile}
          markers={this.props.markers} />
      );

      if (popup.modal) {
        modalPopups.push(popupElement);
      } else {
        modelessPopups.push(popupElement);
      }
    }
    return <>
      <div style={modelessPopupContainerStyle}>{modelessPopups}</div>
      {modalPopups}
    </>;
  }

  public render() {
    const tabPanels = this.props.pagesState.tabs.map((tab: TabInfo, index: number) => {
      const active = this.props.pagesState.activeTab === index;
      const content = this.generateTabContent(tab);
      return (
        <TabPanel key={tab.id} active={active}>
          {content}
          {this.generatePopups(tab)}
        </TabPanel>
      );
    });

    return <>
      <div id="main" style={style}>
        <SimpleResizeSensor onResize={this.handleResize} />
        {tabPanels}
      </div>
    </>;
  }
}

const mapStateToProps = (state: AppStates) => ({
  pagesState: state.pages,
  preferenceState: state.preference,
  drawingState: state.integratedDrawing,
  drawingToolState: state.drawingTool,
  pictureStorage: state.pictureStorage,
  uimodeState: state.uimode,
  tabPanelAreaRect: state.view.tabPanelAreaRect,
  publication: state.publication,
  installedComponents: state.installedComponents,
  viewState: state.view,
  markerTool: state.markerTool,
  markers: state.markers,
});

export default connect(mapStateToProps)(TabbedViewer);
